import { useCallback, useEffect, useState } from 'react'

/**
 * `torch` is not in the standard MediaTrackConstraintSet, but it is what Chrome on
 * Android implements — declared here rather than cast away, so the shape stays checked.
 */
interface TorchConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean
}

export interface Torch {
  /** False means the lamp cannot be driven here — hide the control rather than disable it. */
  supported: boolean
  on: boolean
  toggle: () => void
}

/**
 * The torch is a constraint on the live video track, not a separate device, so it only
 * works while the camera is running. Low-light aisles are the reason it exists.
 */
export function useTorch(track: MediaStreamTrack | null, preferOn = false): Torch {
  // A manual override is kept for callers that expose a live torch button. When there is
  // no override, the saved setting remains the source of truth even if it loads later.
  const [override, setOverride] = useState<boolean | null>(null)
  const requested = override ?? preferOn

  const supported = track !== null && 'torch' in track.getCapabilities()

  // A fresh track starts dark, so the request has to be re-applied every time the camera
  // restarts — which is why this is an effect and not a one-shot call in the toggle.
  useEffect(() => {
    if (!track || !supported) {
      return
    }

    const torch: TorchConstraintSet = { torch: requested }

    track.applyConstraints({ advanced: [torch] }).catch(() => {
      // The lamp may be held by another app. Drop the request rather than reporting a
      // state the hardware is not in.
      setOverride(false)
    })
  }, [track, supported, requested])

  const toggle = useCallback(() => {
    setOverride((current) => !(current ?? preferOn))
  }, [preferOn])

  // Derived, so an unsupported track can never read as lit.
  return { supported, on: supported && requested, toggle }
}
